// frontend/src/components/AGTFin/utils/AGTFloatingChat.js
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Global, css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
  alpha,
  InputBase,
  ClickAwayListener
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Minimize2,
  ArrowUp,
  ArrowLeft,
  Wallet,
  Landmark,
  TrendingUp
} from 'lucide-react';
import {
  createNewChat,
  getChatMessages,
  getChatContext,
  sendMessageToAGTStream
} from '../../AGTChat/services/AGTChatService';
import ToolIndicator from '../../AGTChat/messages/ToolIndicator';
import AGTButton from '../../common/AGTButton';
import AppleSpinner from '../../common/AppleSpinner';

// Inyección de estilos globales para animaciones personalizadas
const GlobalStyles = () => (
  <Global
    styles={css`
      @keyframes fade_eiknz_10 {
        0% {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      ._fadeIn_eiknz_10 {
        animation: fade_eiknz_10 var(--duration, 0s) ease forwards calc(10ms + var(--delay, 0s));
        opacity: 0;
      }
      @keyframes fade_1frq2_1 {
        0% {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      ._markdown_1frq2_10.markdown .katex-display {
        animation: fade_1frq2_1 0.4s ease 50ms forwards;
        opacity: 0;
      }
    `}
  />
);

// Sistema de colores (actualizado para AGT Fin)
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#1F2937', base: '#F2F2F7' } // Neutro para AGT Fin
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#E5E7EB', base: '#272729' } // Neutro para AGT Fin
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const c = mode === 'light' ? appleColors.light : appleColors.dark;

  if (isWallpaperMode) {
    return {
      background: 'rgba(128, 128, 128, 0.25)',
      textDefault: '#FFFFFF',
      textSecondary: 'rgba(255, 255, 255, 0.7)',
      cardBackground: 'rgba(255, 255, 255, 0.12)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      accent: '#FFFFFF',
      success: '#32D74B',
      error: '#FF453A',
      warning: '#FF9F0A',
      isWallpaperMode: true
    };
  }

  return {
    background: c.bg.primaryElevated,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.ui.base,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500',
    isWallpaperMode: false
  };
};

// Keyframe para la animación de fade in (usado en AnimatedWord)
const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// Componente para palabra animada
const AnimatedWord = styled('span')(({ theme, delay, isPlaying }) => ({
  display: 'inline-block',
  marginRight: '0.25em',
  fontSize: 'inherit',
  opacity: isPlaying ? 0 : 1,
  animation: isPlaying ? `${fadeIn} 0.4s ease forwards ${delay}s` : 'none'
}));

// NUEVO COMPONENTE: FormattedAnimatedMessage
// Componente actualizado para respetar espacios y saltos de línea en markdown
const FormattedAnimatedMessage = ({ content, isPlaying }) => {
  const wordCountRef = useRef(0);
 
  // Función auxiliar para envolver cada palabra en AnimatedWord
  // Memorizamos la función con useCallback
  const wrapWordsInAnimation = useCallback((text) => {
    return text.split(' ').map((word) => {
      const currentIndex = wordCountRef.current++;
      return (
        <AnimatedWord
          key={`${word}-${currentIndex}`}
          delay={0.05 * currentIndex}
          isPlaying={isPlaying}
        >
          {word}{' '}
        </AnimatedWord>
      );
    });
  }, [isPlaying]); // Dependencia: isPlaying, ya que se usa dentro de la función

  // Utilizamos useMemo para memorizar el contenido animado y evitar re-procesarlo en cada render
  const animatedContent = useMemo(() => {
    // Reiniciamos el contador solo cuando el contenido cambia
    wordCountRef.current = 0;
    return (
      <Box style={{ '--duration': '0.4s', '--delay': '0s' }}>
        <ReactMarkdown
          components={{
            p: ({ children, node, ...props }) => (
              <p style={{ marginBottom: '1em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </p>
            ),
            strong: ({ children }) => (
              <strong>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </strong>
            ),
            em: ({ children }) => (
              <em>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </em>
            ),
            li: ({ children }) => (
              <li style={{ marginBottom: '0.5em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </li>
            ),
            h1: ({ children }) => (
              <h1 style={{ marginBottom: '0.75em', marginTop: '1em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 style={{ marginBottom: '0.75em', marginTop: '1em' }}>
                {React.Children.map(children, (child) => {
                  if (typeof child === 'string') {
                    return wrapWordsInAnimation(child);
                  }
                  return child;
                })}
              </h2>
            ),
            pre: ({ children }) => (
              <pre style={{ marginBottom: '1em', marginTop: '0.5em', whiteSpace: 'pre-wrap' }}>
                {children}
              </pre>
            ),
            code: ({ inline, children, ...props }) => {
              if (inline) {
                return <code {...props}>{children}</code>;
              }
              return (
                <code {...props} style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                  {children}
                </code>
              );
            },
            // Asegurarnos de que los saltos de línea se respeten
            br: () => <br />
          }}
        >
          {content}
        </ReactMarkdown>
      </Box>
    );
  }, [content, wrapWordsInAnimation]); // Ahora incluye wrapWordsInAnimation como dependencia

  if (isPlaying) {
    return animatedContent;
  }

  // Versión no animada con espaciados correctos
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p style={{ marginBottom: '1em' }}>{children}</p>,
        li: ({ children }) => <li style={{ marginBottom: '0.5em' }}>{children}</li>,
        h1: ({ children }) => <h1 style={{ marginBottom: '0.75em', marginTop: '1em' }}>{children}</h1>,
        h2: ({ children }) => <h2 style={{ marginBottom: '0.75em', marginTop: '1em' }}>{children}</h2>,
        pre: ({ children }) => <pre style={{ marginBottom: '1em', marginTop: '0.5em', whiteSpace: 'pre-wrap' }}>{children}</pre>,
        code: ({ inline, children, ...props }) => {
          if (inline) {
            return <code {...props}>{children}</code>;
          }
          return (
            <code {...props} style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
              {children}
            </code>
          );
        },
        br: () => <br />
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

// Hook de animación desactivado: no se usa en el floating chat
const useLatestMessage = () => ({
  latestMessageId: null,
  updateLatestMessage: () => {}
});

// Botón circular para enviar, con transición en opacidad
// Botón de envío homologado con ChatViewNest
const SendButton = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '4px'
}));

// Componente unificado para input/botón
const UnifiedInputButton = styled(Box)(({ theme, isexpanded }) => {
  const navColors = navigationColors(theme);
  return {
    width: '360px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: navColors.cardBackground,
    borderRadius: '20px',
    padding: '12px 10px 12px 14px',
    transition: theme.transitions.create(['box-shadow', 'background-color'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    }),
    cursor: 'pointer',
    pointerEvents: 'auto',
    border: `1px solid ${navColors.borderColor}`,
    backdropFilter: navColors.isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: navColors.isWallpaperMode ? 'blur(10px)' : 'none',
    '& *': {
      cursor: isexpanded === 'true' ? 'text' : 'pointer'
    }
  };
});

// Input estilizado
const StyledInputBase = styled(InputBase)(({ theme, isexpanded }) => {
  const navColors = navigationColors(theme);
  return {
    width: '100%',
    color: navColors.textDefault,
    '& .MuiInputBase-input': {
      padding: '0px 6px 0px 2px',
      cursor: isexpanded === 'true' ? 'text' : 'pointer',
      '&::placeholder': {
        color: navColors.textSecondary,
        opacity: 1
      }
    }
  };
});

// Chat container con responsividad
const ChatContainer = styled(Paper)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isSmallScreen = useMediaQuery('(max-height: 700px)');
  
  return {
    borderRadius: theme.shape.borderRadius * 2,
    background: navColors.background,
    width: '360px',
    // Ajuste de altura basado en el tamaño de pantalla
    height: isSmallScreen ? '450px' : '600px',
    maxHeight: '80vh', // Garantiza que nunca exceda el 80% de la altura de la ventana
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
    border: `1px solid ${navColors.borderColor}`,
    backdropFilter: navColors.isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: navColors.isWallpaperMode ? 'blur(10px)' : 'none',
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    })
  };
});

// Encabezado del chat
const ChatHeader = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    padding: theme.spacing(2),
    background: navColors.background,
    borderTopLeftRadius: theme.shape.borderRadius * 2,
    borderTopRightRadius: theme.shape.borderRadius * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    borderBottom: navColors.isWallpaperMode ? `1px solid ${navColors.borderColor}` : 'none'
  };
});

// Área de mensajes con scroll manual (quitamos scrollBehavior: 'smooth')
const MessageArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.mode === 'light' ? '#000' : '#FFF', 0.4),
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  }
}));

// Componente Message con radio homologado a ChatViewNest
const Message = styled(Box)(({ theme, variant, content }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const wallpaperColor = '#2C2C2E';

  const baseStyles = {
    maxWidth: '80%',
    padding: theme.spacing(1.5),
    borderRadius: '16px',
    color: navColors.textDefault,
    textTransform: 'none'
  };

  if (variant === 'user') {
    return {
      ...baseStyles,
      alignSelf: 'flex-end',
      background: isWallpaperMode ? wallpaperColor : navColors.cardBackground,
      color: isWallpaperMode ? '#FFFFFF' : navColors.textDefault,
      ...(isWallpaperMode && {
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      })
    };
  }

  return {
    ...baseStyles,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    width: '100%',
    background: 'transparent',
    color: navColors.isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'light' ? '#000000' : '#FFFFFF'),
  };
});

// Contenedor de prompts para mobile
const PromptContainerMobile = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(1),
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(2),
  '&::-webkit-scrollbar': {
    display: 'none'
  }
}));

// Contenedor de prompts para desktop
const PromptContainerDesktop = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap'
}));

// Tarjeta de prompt
const PromptCard = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    background: navColors.cardBackground,
    color: navColors.textDefault,
    padding: theme.spacing(1),
    borderRadius: '9999px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0
  };
});

// Texto de footer
const FooterText = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(1),
  fontSize: '0.65rem',
  color: '#808080'
}));

// Estilos para el contenido Markdown, ajustando también las listas
const MarkdownContent = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    '& > *:first-of-type': {
      marginTop: 0
    },
    '& > *:last-child': {
      marginBottom: 0
    },
    '& p': {
      margin: '0 0 1em 0', // Añadido margen inferior para separar párrafos
      color: navColors.textDefault,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      whiteSpace: 'pre-line' // Respeta saltos de línea en párrafos
    },
    '& ul, & ol': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1.5), // Añadido espacio después de listas
      paddingLeft: theme.spacing(2),
      '& li': {
        fontSize: '0.875rem',
        marginBottom: '0.5em', // Aumentado espacio entre items de listas
        '&::marker': {
          fontSize: '0.875rem',
          color: navColors.textDefault
        }
      }
    },
    '& code': {
      backgroundColor: alpha(navColors.textDefault, 0.1),
      padding: '2px 4px',
      borderRadius: 4,
      fontSize: '0.875rem'
    },
    '& pre': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(navColors.textDefault, 0.15)
        : alpha(navColors.textDefault, 0.1),
      padding: theme.spacing(1),
      margin: theme.spacing(1, 0), // Añadido margen para código
      borderRadius: 4,
      overflow: 'auto',
      whiteSpace: 'pre-wrap', // Asegura que el texto de código respete saltos
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        fontSize: '0.875rem',
        color: theme.palette.mode === 'dark' ? '#e6e6e6' : 'inherit',
        whiteSpace: 'pre-wrap'
      }
    },
    '& blockquote': {
      borderLeft: `4px solid ${alpha(navColors.textDefault, 0.2)}`,
      margin: theme.spacing(1, 0),
      padding: theme.spacing(0.5, 1), // Ajustado padding para citas
      fontSize: '0.875rem'
    },
    '& a': {
      color: navColors.accent,
      textDecoration: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    '& h1': {
      fontSize: '1.2rem',
      fontWeight: 600,
      margin: theme.spacing(1.5, 0, 1, 0) // Top, right, bottom, left
    },
    '& h2': {
      fontSize: '1.1rem',
      fontWeight: 600,
      margin: theme.spacing(1.5, 0, 1, 0)
    },
    '& h3': {
      fontSize: '1rem',
      fontWeight: 600,
      margin: theme.spacing(1.2, 0, 0.8, 0)
    },
    '& hr': {
      margin: theme.spacing(1.5, 0),
      border: 0,
      borderTop: `1px solid ${alpha(navColors.textDefault, 0.2)}`
    },
    '& br': {
      display: 'block', // Asegura que los saltos de línea se muestren
      content: '""',
      marginTop: '0.5em' // Añade algo de espacio para los saltos de línea
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      margin: theme.spacing(1, 0)
    }
  };
});

// FUNCION: renderMessageContent actualizada para el asistente médico
const renderMessageContent = (message, navColors, assistantTextColor) => {
  const getToolsForMessage = () => {
    if (!message) return [];
    if (Array.isArray(message.toolsUsed) && message.toolsUsed.length > 0) {
      return message.toolsUsed;
    }
    if (Array.isArray(message.metadata?.executedTools) && message.metadata.executedTools.length > 0) {
      return message.metadata.executedTools;
    }
    return [];
  };

  const splitContentByToolMarkers = (content) => {
    if (typeof content !== 'string') {
      return { parts: [{ type: 'text', content: '' }], hasMarkers: false };
    }

    const markerRegex = /__TOOL_MARKER_(.*?)__|TOOL_MARKER_([A-Za-z0-9_-]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let hasMarkers = false;

    while ((match = markerRegex.exec(content)) !== null) {
      hasMarkers = true;
      const markerStart = match.index;
      if (markerStart > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, markerStart)
        });
      }

      const toolId = match[1] || match[2];
      parts.push({ type: 'tool', toolId });
      lastIndex = markerStart + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }

    return { parts, hasMarkers };
  };

  if (message.type === 'assistant') {
    const { parts, hasMarkers } = splitContentByToolMarkers(message.content);
    if (hasMarkers) {
      const tools = getToolsForMessage();
      const toolsMap = {};
      tools.forEach((tool) => {
        toolsMap[tool.toolId] = tool;
      });

      return (
        <MarkdownContent className="markdown _markdown_1frq2_10">
          {parts.map((part, index) => {
            if (part.type === 'text') {
              if (!part.content.trim()) {
                return null;
              }
              return (
                <Box key={`text-${index}`}>
                  <FormattedAnimatedMessage content={part.content} isPlaying={false} />
                </Box>
              );
            }

            if (part.type === 'tool') {
              const tool = toolsMap[part.toolId];
              if (!tool) {
                return null;
              }
              return (
                <Box key={`tool-${part.toolId}`} sx={{ my: 2 }}>
                  <ToolIndicator
                    toolName={tool.toolName || tool.toolId || 'Tool'}
                    status={tool.status || 'completed'}
                    error={tool.error}
                    input={tool.input}
                    executionTime={tool.executionTime}
                    phases={tool.phases}
                    isFloatingChat
                    navigationOverride={
                      tool.toolName === 'NavigateToSection' && tool.input?.section === 'fin'
                        ? { section: 'chat', label: 'Back to AGT Chat' }
                        : null
                    }
                  />
                </Box>
              );
            }

            return null;
          })}
        </MarkdownContent>
      );
    }

    // Aseguramos que los saltos de línea del markdown se procesen correctamente
    return (
      <MarkdownContent className="markdown _markdown_1frq2_10">
        <FormattedAnimatedMessage content={message.content} isPlaying={false} />
      </MarkdownContent>
    );
  }

  // Para mensajes del usuario, implementamos un manejo básico de saltos de línea
  return (
    <Typography 
      variant="body2" 
      sx={{ 
        color: assistantTextColor, 
        textTransform: 'none',
        whiteSpace: 'pre-line' // Permite que los saltos de línea se muestren correctamente
      }}
    >
      {message.content}
    </Typography>
  );
};

// Componente principal AGTFloatingChat
const AGTFloatingChat = ({ user }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navColors = navigationColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExpanded, setIsExpanded] = useState(false); // Inicialmente colapsado
  const [messages, setMessages] = useState([
    {
      type: 'assistant',
      content: t('agtfin.floatingChat.fin.initialMessage')
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPollingForAssistant, setIsPollingForAssistant] = useState(false);
  const messagesEndRef = useRef(null);
  const messageAreaRef = useRef(null);
  const inputRef = useRef(null);
  const shouldFocusRef = useRef(false); // Ref para controlar cuando debemos hacer focus
  const assistantTextColor = theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : (theme.palette.mode === 'light' ? '#000000' : '#FFFFFF');
  const arrowColor = inputValue.trim() ? 'white' : navColors.textSecondary;
  // Bandera para controlar si se debe hacer scroll al añadir un mensaje
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const chatIdRef = useRef(null);
  const contextRef = useRef([]);
  const toolsRef = useRef({});
  const pollTimeoutRef = useRef(null);

  const selectedModel = useMemo(() => {
    return localStorage.getItem('selectedModel') || 'agt-nest-20250314';
  }, []);

  const selectedPersonality = useMemo(() => {
    return localStorage.getItem('selectedPersonality') || 'normal';
  }, []);

  const extractMessageContent = useCallback((message) => {
    if (!message) return '';
    const rawText = message.text ?? message.content ?? '';
    if (typeof rawText === 'string') {
      const trimmed = rawText.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (typeof parsed?.text === 'string') {
            return parsed.text;
          }
        } catch (error) {
          // Fallback a texto plano si no es JSON valido
        }
      }
      return rawText;
    }
    if (rawText && typeof rawText.text === 'string') {
      return rawText.text;
    }
    return '';
  }, []);

  const normalizeContextMessage = useCallback((message) => {
    if (!message) return null;
    const role = message.role === 'user' || message.isUser ? 'user' : 'assistant';
    const content = extractMessageContent(message);
    if (!content) return null;
    return {
      type: role,
      content,
      toolsUsed: message.toolsUsed,
      metadata: message.metadata
    };
  }, [extractMessageContent]);

  // Función para hacer scroll hasta el final
  const scrollToBottom = useCallback(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, []);

  // Efecto para hacer autofocus en el input cuando se abre el chat
  useEffect(() => {
    if (isExpanded && inputRef.current && shouldFocusRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current.focus();
          shouldFocusRef.current = false;
        }, 50);
      });
    }
  }, [isExpanded]);

  // Efecto para hacer scroll al fondo cuando se abre el chat
  useEffect(() => {
    if (isExpanded && messageAreaRef.current) {
      scrollToBottom();
    }
  }, [isExpanded, scrollToBottom]);

  // Efecto para realizar scroll solo cuando es necesario
  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, scrollToBottom]);

  // Sin animaciones: no se rastrea el último mensaje

  useEffect(() => {
    const loadChatContext = async () => {
      if (!user?.uid) return;
      const navigationSource = localStorage.getItem('agtFinNavigationSource');
      const navigationChatId = localStorage.getItem('agtFinNavigationChatId');
      if (navigationSource !== 'tool') {
        return;
      }

      shouldFocusRef.current = true;
      setIsExpanded(true);

      const fallbackChatId = localStorage.getItem('currentChatId');
      const resolvedChatId = navigationChatId || fallbackChatId;
      const maxPollAttempts = 30;
      const pollDelayForAttempt = (attempt) => Math.min(800 + attempt * 200, 2000);

      const pollForAssistantResponse = async (attempt = 0) => {
        if (!resolvedChatId || attempt > maxPollAttempts) {
          setIsPollingForAssistant(false);
          return;
        }
        const latestHistory = await getChatMessages(user.uid, resolvedChatId);
        if (!Array.isArray(latestHistory) || latestHistory.length === 0) {
          return;
        }
        const mappedMessages = latestHistory
          .map((message) => normalizeContextMessage(message))
          .filter(Boolean)
          .map((message) => ({
            ...message,
            content: message.content.replace(/\n{3,}/g, '\n\n')
          }));
        const lastMessage = mappedMessages[mappedMessages.length - 1];
        if (lastMessage && lastMessage.type === 'assistant' && lastMessage.content.trim().length > 0) {
          setMessages(mappedMessages);
          setShouldScrollToBottom(true);
          setIsPollingForAssistant(false);
          return;
        }
        pollTimeoutRef.current = setTimeout(() => {
          pollForAssistantResponse(attempt + 1);
        }, pollDelayForAttempt(attempt));
      };

      if (resolvedChatId) {
        chatIdRef.current = resolvedChatId;

        const fullHistory = await getChatMessages(user.uid, resolvedChatId);
        if (Array.isArray(fullHistory) && fullHistory.length > 0) {
          const mappedMessages = fullHistory
            .map((message) => normalizeContextMessage(message))
            .filter(Boolean)
            .map((message) => ({
              ...message,
              content: message.content.replace(/\n{3,}/g, '\n\n')
            }));

        if (mappedMessages.length > 0) {
          setMessages(mappedMessages);
          setShouldScrollToBottom(true);
        }

        const lastMessage = mappedMessages[mappedMessages.length - 1];
        if (!lastMessage || lastMessage.type === 'user' || lastMessage.content.trim().length === 0) {
          setIsPollingForAssistant(true);
          pollForAssistantResponse();
        }
      }

        const context = await getChatContext(user.uid, resolvedChatId);
        if (Array.isArray(context) && context.length > 0) {
          contextRef.current = context;
        }

        localStorage.removeItem('agtFinNavigationChatId');
      }

      localStorage.removeItem('agtFinNavigationSource');
    };

    loadChatContext();

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      setIsPollingForAssistant(false);
    };
  }, [normalizeContextMessage, user]);

  // Opciones de prompts para el asistente financiero
  const promptOptions = [
    {
      label: t('agtfin.floatingChat.fin.prompts.viewSummary'),
      icon: <Landmark size={16} />
    },
    {
      label: t('agtfin.floatingChat.fin.prompts.registerExpense'),
      icon: <Wallet size={16} />
    },
    {
      label: t('agtfin.floatingChat.fin.prompts.registerIncome'),
      icon: <TrendingUp size={16} />
    }
  ];

  const handleExpand = (e) => {
    e.stopPropagation();
    if (!isExpanded) {
      shouldFocusRef.current = true; // Activamos la bandera para hacer focus
      setIsExpanded(true);
    }
  };

  const handleReturnToChat = (e) => {
    e.stopPropagation();
    setIsExpanded(false);
    if (chatIdRef.current) {
      localStorage.setItem('currentChatId', chatIdRef.current);
    }
    window.dispatchEvent(new CustomEvent('navigateToSection', {
      detail: {
        section: 'chat',
        source: 'floatingChat'
      }
    }));
  };

  const buildContext = useCallback(() => {
    if (contextRef.current && contextRef.current.length > 0) {
      return contextRef.current;
    }
    return messages.map((msg) => ({
      role: msg.type === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));
  }, [messages]);

  const ensureChat = useCallback(async () => {
    if (!user?.uid) {
      throw new Error(t('agtfin.floatingChat.fin.errors.userUnavailable'));
    }
    if (chatIdRef.current) {
      return chatIdRef.current;
    }
    const createdChat = await createNewChat(user.uid, 'personal');
    chatIdRef.current = createdChat.id;
    localStorage.setItem('currentChatId', createdChat.id);
    return createdChat.id;
  }, [user]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;

    const context = buildContext();
    setIsLoading(true);
    toolsRef.current = {};
    setMessages((prev) => [...prev, { type: 'user', content: text }]);
    setShouldScrollToBottom(true);
    setInputValue('');

    let assistantMessageIndex = null;
    setMessages((prev) => {
      const nextMessages = [...prev, { type: 'assistant', content: '' }];
      assistantMessageIndex = nextMessages.length - 1;
      return nextMessages;
    });

    try {
      const activeChatId = await ensureChat();
      await sendMessageToAGTStream(
        text,
        user.uid,
        activeChatId,
        context,
        selectedModel,
        selectedPersonality,
        user.displayName || '',
        user.email || '',
        'alloy',
        (token) => {
          if (typeof token === 'string') {
            setMessages((prev) => {
              const nextMessages = [...prev];
              if (nextMessages[assistantMessageIndex]) {
                nextMessages[assistantMessageIndex] = {
                  ...nextMessages[assistantMessageIndex],
                  content: nextMessages[assistantMessageIndex].content + token
                };
              }
              return nextMessages;
            });
            setShouldScrollToBottom(true);
            return;
          }

          if (!token || typeof token !== 'object') return;

          const updateTools = () => {
            const toolsUsed = Object.values(toolsRef.current);
            setMessages((prev) => {
              const nextMessages = [...prev];
              if (nextMessages[assistantMessageIndex]) {
                nextMessages[assistantMessageIndex] = {
                  ...nextMessages[assistantMessageIndex],
                  toolsUsed
                };
              }
              return nextMessages;
            });
          };

          const upsertTool = (toolId, updates) => {
            if (!toolId) return;
            const existing = toolsRef.current[toolId] || { toolId };
            toolsRef.current[toolId] = { ...existing, ...updates };
            updateTools();
          };

          switch (token.type) {
            case 'tool_marker': {
              if (token.marker) {
                setMessages((prev) => {
                  const nextMessages = [...prev];
                  const target = nextMessages[assistantMessageIndex];
                  if (target && !target.content.includes(token.marker)) {
                    nextMessages[assistantMessageIndex] = {
                      ...target,
                      content: `${target.content}\n${token.marker}\n`
                    };
                  }
                  return nextMessages;
                });
              }
              upsertTool(token.toolId, {
                toolName: token.toolName,
                status: 'running'
              });
              break;
            }
            case 'tool_use':
            case 'tool_start':
              upsertTool(token.toolId, {
                toolName: token.toolName,
                status: 'running',
                input: token.input || toolsRef.current[token.toolId]?.input
              });
              break;
            case 'tool_executing':
              upsertTool(token.toolId, { toolName: token.toolName, status: 'running' });
              break;
            case 'tool_progress': {
              const existingPhases = toolsRef.current[token.toolId]?.phases || [];
              const nextPhases = token.phase
                ? [...existingPhases, { phase: token.phase, elapsed: token.elapsed }]
                : existingPhases;
              upsertTool(token.toolId, {
                toolName: token.toolName,
                status: 'running',
                phases: nextPhases
              });
              break;
            }
            case 'tool_complete':
              upsertTool(token.toolId, {
                toolName: token.toolName,
                status: 'completed',
                executionTime: token.executionTime,
                input: token.input || toolsRef.current[token.toolId]?.input,
                phases: token.phases || toolsRef.current[token.toolId]?.phases
              });
              break;
            case 'tool_error':
              upsertTool(token.toolId, {
                toolName: token.toolName,
                status: 'error',
                error: token.error,
                phases: token.phases || toolsRef.current[token.toolId]?.phases
              });
              break;
            default:
              break;
          }
        },
        (result) => {
          contextRef.current = Array.isArray(result.updatedContext) ? result.updatedContext : contextRef.current;
          if (result.chatId && !chatIdRef.current) {
            chatIdRef.current = result.chatId;
            localStorage.setItem('currentChatId', result.chatId);
          }
          setMessages((prev) => {
            const nextMessages = [...prev];
            if (assistantMessageIndex !== null && nextMessages[assistantMessageIndex]) {
              nextMessages[assistantMessageIndex] = {
                ...nextMessages[assistantMessageIndex],
                content: (nextMessages[assistantMessageIndex].content || '')
                  .replace(/\\n{3,}/g, '\\n\\n')
              };
            }
            return nextMessages;
          });
          if (Object.keys(toolsRef.current).length > 0) {
            const toolsUsed = Object.values(toolsRef.current);
            setMessages((prev) => {
              const nextMessages = [...prev];
              if (assistantMessageIndex !== null && nextMessages[assistantMessageIndex]) {
                nextMessages[assistantMessageIndex] = {
                  ...nextMessages[assistantMessageIndex],
                  toolsUsed
                };
              }
              return nextMessages;
            });
          }
          setIsLoading(false);
        },
        (error) => {
          setMessages((prev) => [
            ...prev,
            { type: 'assistant', content: t('agtfin.floatingChat.fin.errors.processingError') }
          ]);
          setIsLoading(false);
        }
      );
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { type: 'assistant', content: t('agtfin.floatingChat.fin.errors.processingError') }
      ]);
      setIsLoading(false);
    }
  }, [buildContext, ensureChat, isLoading, selectedModel, selectedPersonality, user]);

  const handlePromptClick = async (promptText) => {
    await sendMessage(promptText);
  };

  const handleSendMessage = async () => {
    await sendMessage(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <GlobalStyles />
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <ClickAwayListener onClickAway={() => setIsExpanded(false)}>
          <Box>
            {isExpanded && (
              <Box sx={{ marginBottom: 2 }}>
                <ChatContainer onMouseDown={(e) => e.stopPropagation()}>
                  <ChatHeader>
                    <IconButton
                      size="small"
                      onClick={handleReturnToChat}
                      sx={{
                        color: navColors.textDefault,
                        position: 'absolute',
                        left: theme.spacing(2)
                      }}
                    >
                      <ArrowLeft size={18} />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Wallet size={20} style={{ marginRight: 8, color: navColors.accent }} />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          textTransform: 'none',
                          color: navColors.textDefault,
                          fontWeight: 500
                        }}
                      >
                        {t('agtfin.floatingChat.fin.title')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(false);
                      }}
                      sx={{
                        color: navColors.textDefault,
                        position: 'absolute',
                        right: theme.spacing(2)
                      }}
                    >
                      <Minimize2 size={18} />
                    </IconButton>
                  </ChatHeader>

                  <MessageArea ref={messageAreaRef} onMouseDown={(e) => e.stopPropagation()}>
                    {messages.map((message, index) => (
                      <Message key={index} variant={message.type} content={message.content}>
                        {renderMessageContent(
                          message,
                          navColors,
                          assistantTextColor
                        )}
                      </Message>
                    ))}
                    {(isLoading || isPollingForAssistant) && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Box sx={{ transform: 'scale(0.66)' }}>
                          <AppleSpinner />
                        </Box>
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </MessageArea>

                  {messages.length === 1 &&
                    (isMobile ? (
                      <PromptContainerMobile onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <PromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </PromptCard>
                        ))}
                      </PromptContainerMobile>
                    ) : (
                      <PromptContainerDesktop onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <PromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </PromptCard>
                        ))}
                      </PromptContainerDesktop>
                    ))}

                  <Box sx={{ p: 1 }}>
                  </Box>

                  <FooterText>
                    {t('agtfin.floatingChat.fin.disclaimer')}
                  </FooterText>
                </ChatContainer>
              </Box>
            )}

            {/* Input flotante con manejo mejorado del click */}
            <UnifiedInputButton
              isexpanded={isExpanded.toString()}
              onMouseDown={handleExpand} // Usamos la nueva función de manejo específica
            >
              <StyledInputBase
                inputRef={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('agtfin.floatingChat.fin.placeholder')}
                disabled={!isExpanded}
                multiline
                maxRows={4}
                isexpanded={isExpanded.toString()}
                sx={{ pointerEvents: isExpanded ? 'auto' : 'none' }}
              />
              {isExpanded ? (
                <SendButton
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <AGTButton
                    variant="primary"
                    icon={<ArrowUp size={20} />}
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    size="medium"
                    sx={{
                      width: '36px',
                      height: '36px',
                      minWidth: '36px',
                      minHeight: '36px',
                      borderRadius: '10px'
                    }}
                  />
                </SendButton>
              ) : (
                <SendButton sx={{ pointerEvents: 'none' }}>
                  <AGTButton
                    variant="primary"
                    icon={<ArrowUp size={20} />}
                    onClick={() => {}}
                    disabled={false}
                    size="medium"
                    sx={{
                      width: '36px',
                      height: '36px',
                      minWidth: '36px',
                      minHeight: '36px',
                      borderRadius: '10px'
                    }}
                  />
                </SendButton>
              )}
            </UnifiedInputButton>
          </Box>
        </ClickAwayListener>
      </Box>
    </>
  );
};

export default AGTFloatingChat;
